import { useEffect, useState } from 'react';
import { useLayoutSlots } from '@app/layouts/layoutContext';
import { useAppStore } from '@app/store/useAppStore';
import { useKey } from '@app/hooks/useKey';
import { useScale } from '@hooks/useScale';
import { useCagedWindow } from '@hooks/useCagedWindow';
import { useArpeggioSemitones } from '@hooks/useArpeggioSemitones';
import { RootDropdown } from '@components/RootDropdown';
import { ScaleDropdown } from '@components/ScaleDropdown';
import { ScaleDiagram } from '@components/ScaleDiagram';
import { CagedSelector } from '@components/CagedSelector';
import { ArpeggioButton } from '@components/ArpeggioButton';
import { ArpeggioSheet } from '@components/ArpeggioSheet';
import { getTuningById } from '@theory/tunings';
import { INTERVAL_BY_SEMITONE } from '@theory/intervals';
import type { CagedLetter } from '@theory/caged';
import type { ArpeggioSelection } from '@theory/arpeggios';
import styles from './ScalesRoute.module.css';

export function ScalesRoute() {
  const { setCenter, setRight } = useLayoutSlots();
  const keyId = useAppStore((s) => s.selectedKeyId);
  const scaleId = useAppStore((s) => s.selectedScaleId);
  const tuningId = useAppStore((s) => s.selectedTuningId);
  const isLeftHanded = useAppStore((s) => s.isLeftHanded);
  const [caged, setCaged] = useState<CagedLetter | null>(null);
  const [arpeggio, setArpeggio] = useState<ArpeggioSelection>(null);
  const [arpeggioSheetOpen, setArpeggioSheetOpen] = useState(false);

  const key = useKey(keyId);
  const rootForScale = key?.root ?? null;
  const scale = useScale(rootForScale, scaleId);
  const cagedWindow = useCagedWindow(rootForScale, caged);
  const { arpeggioRootNote, semitoneSet: arpeggioSemitoneSet } =
    useArpeggioSemitones(arpeggio, rootForScale, scaleId);
  const tuning = getTuningById(tuningId);

  useEffect(() => {
    setCenter(
      <div className={styles.center}>
        <div className={styles.controls}>
          <div className={styles.controlGroup}>
            <span className={styles.controlLabel}>Key</span>
            <RootDropdown />
          </div>
          <div className={styles.controlGroup}>
            <span className={styles.controlLabel}>Scale</span>
            <ScaleDropdown />
          </div>
          <div className={styles.spacer} />
          <CagedSelector value={caged} onChange={setCaged} />
          <ArpeggioButton
            selection={arpeggio}
            scaleRootNote={rootForScale}
            scaleId={scaleId}
            onPress={() => setArpeggioSheetOpen(true)}
          />
        </div>

        {scale && key ? (
          <>
            <div className={styles.notesRow}>
              {scale.notes.map((n, i) => {
                const colour =
                  INTERVAL_BY_SEMITONE[n.semitones]?.colour ?? 'var(--colour-interval-other)';
                return (
                  <span
                    key={`${n.note}-${i}`}
                    className={styles.notePill}
                    style={{ backgroundColor: colour }}
                  >
                    <span className={styles.deg}>{i + 1}</span>
                    <span className={styles.note}>{n.note}</span>
                  </span>
                );
              })}
            </div>
            <div className={styles.diagramFrame}>
              <ScaleDiagram
                tuning={tuning}
                scaleNotes={scale.notes.map((n) => n.note)}
                chordRoot={key.root}
                isLeftHanded={isLeftHanded}
                cagedWindow={cagedWindow}
                arpeggioSemitoneSet={arpeggioSemitoneSet.size > 0 ? arpeggioSemitoneSet : undefined}
                arpeggioRootNote={arpeggioRootNote ?? undefined}
              />
            </div>
          </>
        ) : (
          <p className={styles.empty}>Pick a key to see its scale.</p>
        )}
        <ArpeggioSheet
          open={arpeggioSheetOpen}
          selection={arpeggio}
          scaleRootNote={rootForScale}
          scaleId={scaleId}
          onSelect={setArpeggio}
          onClose={() => setArpeggioSheetOpen(false)}
        />
      </div>,
    );

    // Right pane intentionally empty — Scales mode uses the full width for the fretboard.
    setRight(null);

    return () => {
      setCenter(null);
      setRight(null);
    };
  }, [
    scale,
    key,
    tuning,
    isLeftHanded,
    caged,
    cagedWindow,
    arpeggio,
    arpeggioRootNote,
    arpeggioSemitoneSet,
    arpeggioSheetOpen,
    rootForScale,
    scaleId,
    setCenter,
    setRight,
  ]);

  return null;
}
